import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { API_BASE_URL } from '../api/client';
import { exportUsersToCSV } from '../utils/csvExport';
import { apiClient } from '../api/client';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  enabled: boolean;
  createdAt: string;
}

export default function AdminUsersPage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [newRole, setNewRole] = useState<string>('');

  const { data: users, isLoading, error } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Failed to fetch users: ${response.status}`);
      }
      return response.json();
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: string }) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
      });
      if (!response.ok) throw new Error('Failed to update role');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditingUser(null);
      setNewRole('');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete user');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  if (isLoading) {
    return (
      <div className="text-white text-center py-20">
        <div className="text-xl">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-white">
        <h1 className="text-3xl font-bold" style={{ fontFamily: "'Orbitron', sans-serif" }}>User Management</h1>
        <div className="mt-6 p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 font-semibold">Error loading users</p>
          <p className="text-red-300 text-sm mt-2">{error.message}</p>
          <p className="text-zinc-400 text-sm mt-2">Check browser console for details</p>
        </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    if (users && users.length > 0) {
      exportUsersToCSV(users);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>User Management</h1>
          <p className="mt-2 text-zinc-400">Manage user accounts and roles</p>
        </div>
        {users && users.length > 0 && (
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/30 hover:text-green-300 transition-all duration-300 font-['Orbitron'] text-sm font-bold tracking-wider uppercase"
          >
            Export CSV
          </button>
        )}
      </div>

      {!users || users.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-zinc-400 text-lg">No users found in the database</p>
        </div>
      ) : (
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Username</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Created</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((user) => (
                <tr key={user.id} className="border-b border-white/5">
                  <td className="px-6 py-4 text-sm text-zinc-300">{user.id}</td>
                  <td className="px-6 py-4 text-sm text-white font-medium">{user.username}</td>
                  <td className="px-6 py-4 text-sm text-zinc-300">{user.email}</td>
                  <td className="px-6 py-4 text-sm">
                    {editingUser === user.id ? (
                      <select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        className="input-field text-sm py-1"
                      >
                        <option value="">Select role...</option>
                        <option value="USER">USER</option>
                        <option value="ADMIN">ADMIN</option>
                      </select>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          user.role === 'ADMIN'
                            ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-cyan-500/20 text-cyan-400'
                        }`}
                      >
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        user.enabled
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {user.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    {editingUser === user.id ? (
                      <>
                        <button
                          onClick={() => {
                            if (newRole) {
                              updateRoleMutation.mutate({ userId: user.id, role: newRole });
                            }
                          }}
                          className="text-green-400 hover:text-green-300 font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingUser(null);
                            setNewRole('');
                          }}
                          className="text-zinc-400 hover:text-zinc-300 font-medium"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingUser(user.id);
                            setNewRole(user.role);
                          }}
                          className="text-cyan-400 hover:text-cyan-300 font-medium"
                        >
                          Edit Role
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete user ${user.username}?`)) {
                              deleteUserMutation.mutate(user.id);
                            }
                          }}
                          className="text-red-400 hover:text-red-300 font-medium"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

